import { cast, flow, getSnapshot, SnapshotIn, toGenerator, types } from 'mobx-state-tree'

import { FetchModel } from '../models/FetchModel'
import {
  ExistingUser,
  ExistingUserEditCloneModel,
  ExistingUserModel,
  NewUserEditCloneModel,
  UserEditCloneModel,
} from '../models/Users/UserModel'
import { Role, RoleModel } from '../models/Users/RoleModel'

import {
  OperationFailure,
  OperationInProgress,
  OperationNotStarted,
  OperationStatusModel,
  OperationSuccess,
} from '../models/common/OperationStatus'
import { FilterModel } from '../models/common/Filters/FilterModel'
import { FilterType } from '../models/common/Filters/FilterType'
import { SelectFieldFilter, SelectFilterIcon } from '../models/common/Filters/SelectFieldFilter'
import type { SearchFieldFilter } from '../models/common/Filters/SearchFieldFilterModel'

const [SEARCH_FILTER_NAME, ROLE_FILTER_NAME] = ['search', 'role']
const USER_FILTER_DEFAULT: SnapshotIn<typeof FilterModel> = {
  fieldFilters: {
    [SEARCH_FILTER_NAME]: { type: FilterType.SEARCH, placeholder: 'Search' },
    [ROLE_FILTER_NAME]: { type: FilterType.SELECT, icon: SelectFilterIcon.UserRole },
  },
}

export const UsersStore = FetchModel.named('UsersStore')
  .props({
    // For now, "users" won't contain new unsaved users. The "new user" row displayed in the table is just a UI trick, not a real user.
    // We don't add it since it doesn't behave as a real user in any way - not adhering to filtering/sorting the table, not displaying user data, and we can't reference it with selectedUser since it has no Id.
    // Also, we don't count it in the users counter on above the table.
    users: types.maybeNull(types.array(ExistingUserModel)),

    roles: types.maybeNull(types.array(RoleModel)),

    selectedUser: types.safeReference(ExistingUserModel),
    userCopyForEditing: types.maybeNull(UserEditCloneModel),

    filters: types.optional(FilterModel, USER_FILTER_DEFAULT),

    fetchStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    saveStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    deleteStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),

    validationStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
  })
  .views((self) => ({
    get getUsersUrl(): string {
      return `${self.baseUrl}users/`
    },
    get getRolesUrl(): string {
      return `${self.baseUrl}users/roles/`
    },
    userUrl(userId: number): string {
      return `${self.baseUrl}users/${userId}/`
    },
    _getFilterByFieldName(fieldName: string) {
      const filter = self.filters.fieldFilters.get(fieldName)
      if (filter === undefined) {
        throw new Error(`filter ${fieldName} must appear`)
      }
      return filter
    },
  }))
  .views((self) => ({
    get searchFilter(): SearchFieldFilter {
      return self._getFilterByFieldName(SEARCH_FILTER_NAME) as SearchFieldFilter
    },
    get roleFilter(): SelectFieldFilter {
      return self._getFilterByFieldName(ROLE_FILTER_NAME) as SelectFieldFilter
    },
  }))
  .views((self) => ({
    get shownUsers(): Array<ExistingUser> | null {
      if (self.users === null) return null

      const searchValue = self.searchFilter.value?.toLowerCase() ?? ''
      const roleFilterValue = self.roleFilter.filterValue as (Role | undefined)
      return self.users
        .filter((user) => user.name?.toLowerCase().includes(searchValue) || user.email?.toLowerCase().includes(searchValue))
        .filter((user) => roleFilterValue === undefined || user.roles.some( role => roleFilterValue.value === role.value))
    },
  }))
  .actions((self) => ({
    _setUsers(users: SnapshotIn<typeof self.users>) {
      self.users = cast(users)

      // selectedUser is a safeReference, so it will automatically change to undefined if the corresponding user is not in the array.
      // However, we must refresh userCopyForEditing manually.
      if (self.userCopyForEditing !== null && self.selectedUser === undefined) {
        self.userCopyForEditing = null
      }
    },
    _setRoles(roles: SnapshotIn<typeof self.roles>) {
      self.roles = cast(roles)
      self.filters.fieldFilters.set(ROLE_FILTER_NAME, {
        type: FilterType.SELECT, 
        icon: SelectFilterIcon.UserRole, 
        options: self.roles?.map(role => ({value: role.value, text: role.text})),
      })
    },
  }))
  .actions((self) => ({
    clearSelectedUser() {
      self.selectedUser = undefined
      self.userCopyForEditing = null
    },
    trySetSelectedUser(userId: number): boolean {
      if (self.users === null) {
        console.warn("Can't select user when no users fetched")
        return false
      }

      const selectedUser = self.users.find((user) => user.id === userId)
      if (selectedUser === undefined) {
        self.selectedUser = undefined
        self.userCopyForEditing = null
        return false
      }

      self.selectedUser = selectedUser
      self.userCopyForEditing = UserEditCloneModel.create(getSnapshot(selectedUser))

      return true
    },
    createNewUser() {
      if (self.users === null) {
        console.error("Can't create user when no users fetched")
        throw new Error("Can't create user when no users fetched")
      }
      self.validationStatus = OperationNotStarted()

      self.selectedUser = undefined
      self.userCopyForEditing = NewUserEditCloneModel.create({
        name: '',
        email: '',
      })
    },
  }))
  .views((self) => ({
    get isAddingUser() {
      return NewUserEditCloneModel.is(self.userCopyForEditing)
    },
  }))
  .actions((self) => ({
    fetchAndSetUsersAndRoles: flow(function* fetchAndSetUsersAndRoles() {
      self.fetchStatus = OperationInProgress()

      const fetchUsersResult = yield* toGenerator(
        self.loadObjectOrError<SnapshotIn<typeof self.users>>(
          self.getUsersUrl,
          {},
          { fetchOperationName: 'Fetch users' },
        ),
      )
      if (fetchUsersResult.error) {
        self.fetchStatus = OperationFailure(fetchUsersResult.error)
        return
      }

      const fetchRolesResult = yield* toGenerator(
        self.loadObjectOrError<SnapshotIn<typeof self.roles>>(
          self.getRolesUrl,
          {},
          { fetchOperationName: 'Fetch roles' },
        ),
      )
      if (fetchRolesResult.error) {
        self.fetchStatus = OperationFailure(fetchRolesResult.error)
        return
      }

      self._setUsers(fetchUsersResult.result)
      self._setRoles(fetchRolesResult.result)

      self.fetchStatus = OperationSuccess()
    }),
  }))
  .actions((self) => ({
    saveEditedUser: flow(function* saveEditedUser() {
      if (self.userCopyForEditing === null) {
        console.warn('No user edited, cannot save')
        throw new Error('No user edited, cannot save it')
      }

      // validation on the inputs the user inserts:
      self.validationStatus = self.userCopyForEditing.isValid ? 
        OperationSuccess() : 
        OperationFailure(Error("User is not valid"))

      if (self.validationStatus.error) {
        return false
      }

      // validation passed, start saving
      self.saveStatus = OperationInProgress()

      const userJson = JSON.stringify(getSnapshot(self.userCopyForEditing))

      const [url, method] = ExistingUserEditCloneModel.is(self.userCopyForEditing)
        ? [self.userUrl(self.userCopyForEditing.id), 'PATCH']
        : [self.getUsersUrl, 'POST']

      const fetchResult = yield* toGenerator(
        self.loadObjectOrError<void>(url, { method, body: userJson }, { fetchOperationName: 'Save user' }),
      )
      if (fetchResult.error) {
        self.saveStatus = OperationFailure(fetchResult.error)
      } else {
        self.saveStatus = OperationSuccess()
        void self
          .fetchAndSetUsersAndRoles() // refresh user list and roles
          .then(() => {
            // make a fresh editing clone with user from `users`, if needed.
            if (
              self.selectedUser !== undefined &&
              ExistingUserEditCloneModel.is(self.userCopyForEditing) &&
              self.selectedUser.id === self.userCopyForEditing.id
            ) {
              self.trySetSelectedUser(self.selectedUser.id)
            }
          })
      }

      return self.saveStatus.isSuccess
    }),
    deleteUser: flow(function* deleteUser(userId: number) {
      self.deleteStatus = OperationInProgress()

      const fetchResult = yield* toGenerator(
        self.loadObjectOrError<void>(self.userUrl(userId), { method: 'DELETE' }, { fetchOperationName: 'Delete user' }),
      )
      if (fetchResult.error) {
        self.deleteStatus = OperationFailure(fetchResult.error)
      } else {
        if (self.selectedUser?.id === userId) {
          self.selectedUser = undefined
          self.userCopyForEditing = null
        }
        self.deleteStatus = OperationSuccess()
        void self.fetchAndSetUsersAndRoles() // refresh user list and roles
      }
    }),
  }))
